const STORAGE_LEFT_HOME = 'databiqs-left-home';
const STORAGE_FULL_INTRO = 'databiqs-intro-full';
/** One replay intro per document reload; navigation.type stays "reload" for the whole session. */
const STORAGE_REPLAY_HANDLED = 'databiqs-replay-intro-handled';

function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isMobileOrCoarsePointer() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;
}

function isSlowConnection() {
  if (typeof navigator === 'undefined') return false;
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return false;
  if (conn.saveData) return true;
  const type = conn.effectiveType;
  return type === 'slow-2g' || type === '2g' || type === '3g';
}

function isLowEndDevice() {
  if (typeof navigator === 'undefined') return false;
  const cores = navigator.hardwareConcurrency || 8;
  const memory = navigator.deviceMemory || 8;
  return cores <= 4 || memory < 4;
}

function isPrerenderCrawl() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /ReactSnap/i.test(ua) || /HeadlessChrome/i.test(ua);
}

export function isNavigationReload() {
  if (typeof performance === 'undefined') return false;
  const nav = performance.getEntriesByType?.('navigation')?.[0];
  return nav?.type === 'reload';
}

if (typeof window !== 'undefined' && isNavigationReload()) {
  try {
    sessionStorage.removeItem(STORAGE_REPLAY_HANDLED);
  } catch {
    /* ignore */
  }
}

/**
 * Canvas 2D required for the orbit particle intro.
 * (Robot Three.js intro assets remain in /public but are not mounted.)
 */
export function canPlayDoorIntro() {
  if (typeof document === 'undefined') return false;
  if (isPrerenderCrawl()) return false;
  try {
    const canvas = document.createElement('canvas');
    return !!canvas.getContext('2d');
  } catch {
    return false;
  }
}

/**
 * Home intro behavior:
 * - full: first homepage load in session (orbit particle + logo reveal)
 * - replay: browser refresh on / (animation again)
 * - skip: client navigation back to / or accessibility / no canvas / prerender crawl
 */
export function getHomeIntroMode() {
  if (typeof window === 'undefined') return 'skip';
  if (isPrerenderCrawl()) return 'skip';
  // First client paint over react-snap HTML — keep markup stable for hydrateRoot.
  if (window.__DATABIQS_HYDRATE_SKIP_INTRO__) return 'skip';
  if (prefersReducedMotion() || !canPlayDoorIntro()) return 'skip';

  const leftHome = sessionStorage.getItem(STORAGE_LEFT_HOME) === '1';
  const replayHandled = sessionStorage.getItem(STORAGE_REPLAY_HANDLED) === '1';

  if (isNavigationReload() && !replayHandled) return 'replay';
  if (leftHome) return 'skip';

  const fullSeen = sessionStorage.getItem(STORAGE_FULL_INTRO) === '1';
  if (!fullSeen) return 'full';

  return 'skip';
}

export function shouldSkipDoorIntro() {
  return getHomeIntroMode() === 'skip';
}

export function markReplayIntroHandled() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(STORAGE_REPLAY_HANDLED, '1');
}

export function markLeftHomeInSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(STORAGE_LEFT_HOME, '1');
  markReplayIntroHandled();
}

export function markFullIntroComplete() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(STORAGE_FULL_INTRO, '1');
}

/** @deprecated use markFullIntroComplete */
export function markDoorIntroSeen() {
  markFullIntroComplete();
}

/** Mobile / slow / low-end — defer heavy homepage videos and chatbot (not the intro). */
export function shouldDeferHeavyVisuals() {
  if (typeof window === 'undefined') return true;
  if (isPrerenderCrawl()) return true;
  return (
    isMobileOrCoarsePointer() ||
    prefersReducedMotion() ||
    isSlowConnection() ||
    isLowEndDevice()
  );
}

function isVerySlowConnection() {
  if (typeof navigator === 'undefined') return false;
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return false;
  if (conn.saveData) return true;
  const type = conn.effectiveType;
  return type === 'slow-2g' || type === '2g';
}

export function prefersReducedMotionOnly() {
  return prefersReducedMotion();
}

/** Accordion videos on mobile + desktop; skip only for a11y or very slow data. */
export function shouldUseSolutionVideos() {
  if (typeof window === 'undefined') return false;
  if (prefersReducedMotion()) return false;
  if (isVerySlowConnection()) return false;
  return true;
}

/** @deprecated Solution tabs preload active + next only (see preloadSolutionVideosForTabs). */
export function shouldPreloadAllSolutionVideos() {
  return false;
}

export function getDeferredRootMargin(desktopMargin = '280px 0px') {
  if (shouldDeferHeavyVisuals()) return '48px 0px';
  return desktopMargin;
}
